import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, useRequireAuth } from '@/hooks/useAuth';

// Layouts
import { AppLayout } from '@/components/layout/AppLayout';
import { ProjectLayout } from '@/components/layout/ProjectLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';

// Landing
import { LandingPage } from '@/pages/Landing';

// Callback
import { CallbackPage } from '@/pages/Callback';

// Dashboard
import { DashboardPage } from '@/pages/dashboard';

// Project Pages
import {
  NewProjectPage,
  ProjectOverviewPage,
  ProjectChatPage,
  ProjectFilesPage,
  ProjectTerminalPage,
  ProjectSettingsPage,
  ProjectDomainsPage,
} from '@/pages/projects';

// Billing
import { SubscriptionPage } from '@/pages/billing/SubscriptionPage';

// Settings
import { ProfilePage, GitHubSettingsPage } from '@/pages/settings';

// Auth
import { GitHubCallbackPage } from '@/pages/auth';

// Admin Pages
import {
  AdminOverviewPage,
  AdminUsersPage,
  AdminProjectsPage,
  AdminCertificatesPage,
  AdminLogsPage,
} from '@/pages/admin';

// Loading
import { PageLoader } from '@/components/ui/Spinner';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized } = useRequireAuth();

  if (!isInitialized) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return null; // useRequireAuth handles redirect to Keycloak
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, hasAnyRole } = useAuth();

  if (!isInitialized) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check if user has admin roles
  if (!hasAnyRole(['admin', 'superadmin'])) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* OAuth Callback */}
      <Route path="/callback" element={<CallbackPage />} />

      {/* GitHub OAuth Callback */}
      <Route path="/auth/github/callback" element={<GitHubCallbackPage />} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/projects" element={<Navigate to="/dashboard" replace />} />
        <Route path="/billing" element={<SubscriptionPage />} />
        <Route path="/settings" element={<ProfilePage />} />
        <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />
        <Route path="/settings/profile" element={<ProfilePage />} />
        <Route path="/settings/github" element={<GitHubSettingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/help" element={<div>Help Page</div>} />
      </Route>

      {/* New Project */}
      <Route
        path="/projects/new"
        element={
          <ProtectedRoute>
            <NewProjectPage />
          </ProtectedRoute>
        }
      />

      {/* Project Routes */}
      <Route
        path="/projects/:slug"
        element={
          <ProtectedRoute>
            <ProjectLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProjectOverviewPage />} />
        <Route path="chat" element={<ProjectChatPage />} />
        <Route path="files" element={<ProjectFilesPage />} />
        <Route path="terminal" element={<ProjectTerminalPage />} />
        <Route path="domains" element={<ProjectDomainsPage />} />
        <Route path="settings" element={<ProjectSettingsPage />} />
      </Route>

      {/* Admin Routes - requires admin or superadmin role */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminOverviewPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="projects" element={<AdminProjectsPage />} />
        <Route path="certificates" element={<AdminCertificatesPage />} />
        <Route path="logs" element={<AdminLogsPage />} />
      </Route>

      {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />

      {/* Catch-all redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return <AppRoutes />;
}

export default App;
